import { useEffect, useState } from 'react';
import { Link, StatusError, StatusOK, StatusWarning, Table, TableColumn } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { IgnoreRow, Status, STATUS_TEXT, statusOf } from './status';

export type Data = { updatedAt?: string; repos: number; rows: IgnoreRow[] };
type Row = IgnoreRow & { status: Status };

const day = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('es-EC') : '—');

const badge = (s: Status) => {
  const text = STATUS_TEXT[s];
  if (s === 'vencida' || s === 'por-vencer') return <StatusError>{text}</StatusError>;
  return s === 'sin-vencimiento' ? <StatusWarning>{text}</StatusWarning> : <StatusOK>{text}</StatusOK>;
};

/** Lee /api/snyk-ignores/ignores (todas o las de un repo). */
export const useSnykIgnores = (slug?: string) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    (async () => {
      const base = await discovery.getBaseUrl('snyk-ignores');
      const res = await fetch(`${base}/ignores${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setData(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [discovery, fetch, slug]);
  return { data, error };
};

/** Tabla de excepciones; las vencidas y las que vencen en menos de 15 días, en rojo y primero. */
export const IgnoresTable = ({ rows, showRepo, title }: { rows: IgnoreRow[]; showRepo?: boolean; title?: string }) => {
  const order: Record<Status, number> = { vencida: 0, 'por-vencer': 1, 'sin-vencimiento': 2, vigente: 3 };
  const data: Row[] = rows.map(r => ({ ...r, status: statusOf(r) })).sort((a, b) => order[a.status] - order[b.status]);
  const columns: TableColumn<Row>[] = [
    ...(showRepo
      ? [
          { title: 'Repo', field: 'slug', render: (r: Row) => <Link to={`https://github.com/${r.slug}/blob/HEAD/.snyk`}>{r.slug.split('/')[1]}</Link> },
          { title: 'Equipo', field: 'owner' },
        ]
      : []),
    {
      title: 'Vulnerabilidad',
      field: 'id',
      render: r => (
        <>
          <Link to={`https://security.snyk.io/vuln/${encodeURIComponent(r.id)}`}>{r.id}</Link>
          {r.path !== '*' && <div style={{ fontSize: 12, opacity: 0.7 }}>{r.path}</div>}
        </>
      ),
    },
    { title: 'Motivo', field: 'reason' },
    { title: 'Vence', field: 'expires', render: r => day(r.expires) },
    { title: 'Estado', field: 'status', render: r => badge(r.status), customFilterAndSearch: (term: string, r: Row) => STATUS_TEXT[r.status].toLowerCase().includes(term.toLowerCase()) },
    { title: 'Quién', field: 'author', render: r => r.author ?? '—' },
    { title: 'Añadida', field: 'addedAt', render: r => day(r.addedAt ?? r.created) },
  ];
  return (
    <Table<Row>
      title={title}
      options={{ paging: rows.length > 25, pageSize: 25, search: true, filtering: false, padding: 'dense' }}
      columns={columns}
      data={data}
      emptyContent={<div style={{ padding: 16 }}>Sin excepciones en el .snyk.</div>}
    />
  );
};
